/**
 * Routing + access guards.
 *
 * `ProtectedRoute` bounces an unauthenticated operator to `/login`, remembering where
 * they were headed. `RequirePermission` guards each page against the mirrored RBAC map,
 * so deep-linking to a page your role cannot use shows a clean "no access" panel rather
 * than a broken screen — the API still enforces, this is only what the console renders.
 */
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useAuth } from './auth/AuthContext.js';
import { adminCanAny } from './contracts/permissions.js';
import type { AdminPermission } from './contracts/types.js';
import { Layout } from './components/Layout.js';
import { Card, Spinner } from './components/ui.js';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { FlagsPage } from './pages/FlagsPage.js';
import { ProvidersPage } from './pages/ProvidersPage.js';
import { ProviderCredentialsPage } from './pages/ProviderCredentialsPage.js';
import { BanksPage } from './pages/BanksPage.js';
import { BillServicesPage } from './pages/BillServicesPage.js';
import { RatesPage } from './pages/RatesPage.js';
import { FeesPage } from './pages/FeesPage.js';
import { AssetRailsPage } from './pages/AssetRailsPage.js';
import { TreasuryOverviewPage } from './pages/TreasuryOverviewPage.js';
import { TreasuryOrdersPage, TreasuryOrderDetailPage } from './pages/TreasuryOrdersPage.js';
import { TreasuryReviewPage } from './pages/TreasuryReviewPage.js';
import { TreasuryRebalancesPage } from './pages/TreasuryRebalancesPage.js';
import { TreasuryThresholdsPage } from './pages/TreasuryThresholdsPage.js';
import { TreasuryReconciliationPage } from './pages/TreasuryReconciliationPage.js';
import { TreasuryCustodySweepPage } from './pages/TreasuryCustodySweepPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { UserDetailPage } from './pages/UserDetailPage.js';
import { KycReviewPage } from './pages/KycReviewPage.js';
import { KycLimitsPage } from './pages/KycLimitsPage.js';
import { GiftcardsPage } from './pages/GiftcardsPage.js';
import { TransactionsPage } from './pages/TransactionsPage.js';
import { BrandAssetsPage } from './pages/BrandAssetsPage.js';
import { SupportInboxPage } from './pages/SupportInboxPage.js';
import { SupportThreadPage } from './pages/SupportThreadPage.js';
import { SupportSettingsPage } from './pages/SupportSettingsPage.js';
import { BusinessSettingsPage } from './pages/BusinessSettingsPage.js';
import { EmailTemplatesPage } from './pages/EmailTemplatesPage.js';
import { EmailTemplateEditorPage } from './pages/EmailTemplateEditorPage.js';
import { EmailSettingsPage } from './pages/EmailSettingsPage.js';
import { EmailBroadcastPage } from './pages/EmailBroadcastPage.js';
import { LegalPage } from './pages/LegalPage.js';
import { PushBroadcastPage } from './pages/PushBroadcastPage.js';
import { BannersPage } from './pages/BannersPage.js';
import type { ReactNode } from 'react';

/**
 * Gift cards is one page over two API grants — `kyc:review` for the redemption queue,
 * `rates:write` for the brand catalogue — so either one opens it and each tab guards its own
 * half. Named here rather than inlined so the pairing is stated once and stays legible.
 */
const GIFTCARD_GRANTS: readonly AdminPermission[] = ['kyc:review', 'rates:write'];

function ProtectedRoute({ children }: { children: ReactNode }): JSX.Element {
  const { session } = useAuth();
  const location = useLocation();
  if (session === null) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

/**
 * Guard a page on one permission — or, where a page genuinely serves two grants, on any of
 * several.
 *
 * The plural case is not a convenience. Gift cards is one page carrying two surfaces the API
 * gates separately: compliance reviews redemptions, finance prices the catalogue. Guarding it
 * on a single grant locks out half the people it is for — and gating on the *narrower* of the
 * two is how the finance desk ended up unable to open the only page that sets its own rates.
 * Any-of admits both, and each surface inside then guards itself, which is where the real
 * distinction lives anyway. The API enforces regardless; this only decides what renders.
 */
function RequirePermission({
  permission,
  children,
}: {
  permission: AdminPermission | readonly AdminPermission[];
  children: ReactNode;
}): JSX.Element {
  const { session } = useAuth();
  if (session !== null && !adminCanAny(session.me.role, permission)) {
    return (
      <Card title="No access">
        <p className="muted">
          Your role does not have access to this area. If you believe this is wrong, ask a super
          admin to review your permissions.
        </p>
      </Card>
    );
  }
  return <>{children}</>;
}

export function App(): JSX.Element {
  const { session, booting } = useAuth();

  /*
   * A remembered session being renewed on boot.
   *
   * Rendering the routes here would flash the login screen at an operator who is about to be
   * signed in — the session is not live for the half-second the refresh takes, so
   * `ProtectedRoute` would bounce them and then bounce them back. One quiet panel instead.
   */
  if (booting) {
    return (
      <div className="boot">
        <Spinner />
        <p className="muted">Signing you back in…</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={session !== null ? <Navigate to="/" replace /> : <LoginPage />} />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="flags" element={<RequirePermission permission="flags:read"><FlagsPage /></RequirePermission>} />
        <Route path="providers" element={<RequirePermission permission="providers:read"><ProvidersPage /></RequirePermission>} />
        <Route path="providers/credentials" element={<RequirePermission permission="providers:read"><ProviderCredentialsPage /></RequirePermission>} />
        <Route path="banks" element={<RequirePermission permission="providers:read"><BanksPage /></RequirePermission>} />
        {/* The bill tiles: the artwork each is drawn from, and whether a biller is offered at
            all. Superadmin, on the read as well as the writes, because that is what the API
            enforces on all four of its bill-service routes — the list included — for the same
            reason the banners and the brand assets are superadmin: this is operator-authored
            artwork and copy that lands on every customer's Bills tab. It used to be guarded on
            `providers:read`, which every role holds, so three roles could reach a page whose
            first request answered a permission error. */}
        <Route path="bills/services" element={<RequirePermission permission="admins:write"><BillServicesPage /></RequirePermission>} />
        <Route path="rates" element={<RequirePermission permission="rates:read"><RatesPage /></RequirePermission>} />
        {/* What the house charges on top of the market: the spread, every basis-point fee,
            and the bill schedule. `rates:read` on the read — support answering "why did my
            swap cost more than the app quoted?" needs to see the fee that priced it — and
            `rates:write` on the writes, which the page enforces itself. */}
        <Route path="fees" element={<RequirePermission permission="rates:read"><FeesPage /></RequirePermission>} />
        {/* Deposit minimums and confirmation depth. `rates:read` on the read — support
            answering "why was my deposit never credited?" needs to see the threshold that
            refused it — and `rates:write` on the writes, which the page enforces itself. */}
        <Route path="assets/rails" element={<RequirePermission permission="rates:read"><AssetRailsPage /></RequirePermission>} />
        {/* The treasury desk. Reads are `rates:read`, which every role that can see the money
            pages already holds; the writes behind them are enforced by the API on `finance`
            rather than by hiding a route, because a hidden route is not a permission. */}
        <Route path="treasury" element={<RequirePermission permission="rates:read"><TreasuryOverviewPage /></RequirePermission>} />
        <Route path="treasury/orders" element={<RequirePermission permission="rates:read"><TreasuryOrdersPage /></RequirePermission>} />
        <Route path="treasury/orders/:id" element={<RequirePermission permission="rates:read"><TreasuryOrderDetailPage /></RequirePermission>} />
        <Route path="treasury/review" element={<RequirePermission permission="rates:read"><TreasuryReviewPage /></RequirePermission>} />
        <Route path="treasury/rebalances" element={<RequirePermission permission="rates:read"><TreasuryRebalancesPage /></RequirePermission>} />
        <Route path="treasury/thresholds" element={<RequirePermission permission="rates:read"><TreasuryThresholdsPage /></RequirePermission>} />
        <Route path="treasury/reconciliation" element={<RequirePermission permission="rates:read"><TreasuryReconciliationPage /></RequirePermission>} />
        <Route path="treasury/custody-sweep" element={<RequirePermission permission="rates:read"><TreasuryCustodySweepPage /></RequirePermission>} />
        {/* Finance-only, unlike the sweep beside it: this one leaves the platform. */}
        <Route path="users" element={<RequirePermission permission="users:read"><UsersPage /></RequirePermission>} />
        <Route path="users/:id" element={<RequirePermission permission="users:read"><UserDetailPage /></RequirePermission>} />
        <Route path="kyc" element={<RequirePermission permission="kyc:read"><KycReviewPage /></RequirePermission>} />
        {/* The ceilings are compliance policy — the same grant that decides a tier decides
            what that tier is worth — so the read is `kyc:review`, not `kyc:read`. */}
        <Route path="kyc/limits" element={<RequirePermission permission="kyc:review"><KycLimitsPage /></RequirePermission>} />
        <Route path="giftcards" element={<RequirePermission permission={GIFTCARD_GRANTS}><GiftcardsPage /></RequirePermission>} />
        <Route path="transactions" element={<RequirePermission permission="transactions:read"><TransactionsPage /></RequirePermission>} />
        <Route path="support" element={<RequirePermission permission="users:read"><SupportInboxPage /></RequirePermission>} />
        <Route path="support/threads/:id" element={<RequirePermission permission="users:read"><SupportThreadPage /></RequirePermission>} />
        <Route path="support-settings" element={<RequirePermission permission="admins:write"><SupportSettingsPage /></RequirePermission>} />
        <Route path="settings/business" element={<RequirePermission permission="admins:write"><BusinessSettingsPage /></RequirePermission>} />
        <Route path="email-templates" element={<RequirePermission permission="admins:write"><EmailTemplatesPage /></RequirePermission>} />
        <Route path="email-templates/:key" element={<RequirePermission permission="admins:write"><EmailTemplateEditorPage /></RequirePermission>} />
        {/* The mail server, the branding every message wears, and the shared image library.
            `admins:write` like every other white-label surface; the API gates it on superadmin
            and the page renders read-only for anyone else. */}
        <Route path="email-settings" element={<RequirePermission permission="admins:write"><EmailSettingsPage /></RequirePermission>} />
        {/* Outward-facing and irreversible, so it is guarded exactly as the push broadcast is. */}
        <Route path="email-broadcast" element={<RequirePermission permission="admins:write"><EmailBroadcastPage /></RequirePermission>} />
        <Route path="legal" element={<RequirePermission permission="admins:write"><LegalPage /></RequirePermission>} />
        <Route path="push" element={<RequirePermission permission="admins:write"><PushBroadcastPage /></RequirePermission>} />
        <Route path="banners" element={<RequirePermission permission="admins:write"><BannersPage /></RequirePermission>} />
        <Route path="brand" element={<RequirePermission permission="admins:write"><BrandAssetsPage /></RequirePermission>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
